/**
 * Dashboard widgets API (manual client - not yet in generated OpenAPI).
 * @see https://api.beclassmanager.com/scalar/v1#tag/widgets
 */

import { widgetsDebug } from "@/lib/dashboard/widgets-debug-log";
import { getActiveRoleRequestHeaders } from "@/lib/utils/api/active-role-header";
import { BASE_PATH } from "@/openapi/generated/runtime";

const apiBaseUrl =
  (typeof process !== "undefined" &&
    process.env.NEXT_PUBLIC_API_URL?.replace(/\/+$/, "")) ||
  BASE_PATH;

/** Must match API enum `WidgetCategory` (see OpenAPI: "Kpi" - not "KPI"). */
export type WidgetSettingCategoryDto = "Kpi" | "Regular";

export interface WidgetSettingFromApi {
  id: number;
  key: string;
  name: string;
  displayName: string | null;
  /** Canonical API field for KPI / Regular sequencing (matches OpenAPI `GetWidgetSettingsResponse`). */
  sortOrder?: number | string | null;
  SortOrder?: number | string | null;
  /** Legacy name some backends used before `sortOrder`. */
  displayOrder?: number;
  DisplayOrder?: number;
  /** Role strings as returned by the API (typically lowercase). */
  roles?: string[];
  widgetRoles?: string[];
  /** Per-role display order. Absent on older API responses. */
  roleOrders?: WidgetRoleOrderFromApi[];
  RoleOrders?: WidgetRoleOrderFromApi[];
  category?: string;
}

export interface WidgetRoleOrderFromApi {
  role?: string;
  Role?: string;
  sortOrder?: number | string | null;
  SortOrder?: number | string | null;
}

export interface WidgetPutBody {
  name: string;
  displayName: string | null;
  category: WidgetSettingCategoryDto;
  widgetRoles: string[];
  /**
   * API `sortOrder` - legacy shared order. Per-portal order is `roleOrders`.
   * Send `null` only when the backend explicitly allows omitting/clamping.
   */
  sortOrder?: number | null;
  roleOrders?: Array<{ role: string; sortOrder: number }>;
}

export interface WidgetDataItemFromApi {
  id: number;
  key: string;
  name: string;
  displayName: string | null;
  category: string;
  data?: {
    /** e.g. "count" */
    $type?: string;
    total?: number;
    [key: string]: unknown;
  };
}

function authHeaders(accessToken: string, tenantId: number): HeadersInit {
  return {
    ...getActiveRoleRequestHeaders(),
    Authorization: `Bearer ${accessToken}`,
    "X-Tenant-ID": String(tenantId),
    Accept: "application/json",
  };
}

async function parseJsonSafe<T>(res: Response): Promise<T | null> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

export async function fetchWidgetSettings(
  accessToken: string,
  tenantId: number,
): Promise<WidgetSettingFromApi[]> {
  const res = await fetch(`${apiBaseUrl}/api/widgets/settings`, {
    method: "GET",
    headers: authHeaders(accessToken, tenantId),
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(
      `GET /api/widgets/settings failed: ${res.status} ${body || res.statusText}`,
    );
  }

  const parsed = await parseJsonSafe<WidgetSettingFromApi[]>(res);
  const rows = Array.isArray(parsed) ? parsed : [];
  widgetsDebug("GET /api/widgets/settings →", {
    tenantId,
    count: rows.length,
    summary: rows.map((r) => ({
      id: r.id,
      key: r.key,
      sortOrder: r.sortOrder ?? r.SortOrder,
      roles: r.roles ?? r.widgetRoles,
      apiCategory: r.category ?? null,
    })),
    rawPreview: rows,
  });
  return rows;
}

export async function putWidgetSetting(
  accessToken: string,
  tenantId: number,
  id: number,
  body: WidgetPutBody,
): Promise<void> {
  /** JSON body aligns with OpenAPI `UpdateWidgetRequest` (incl. `sortOrder`). */
  const sortPayload =
    typeof body.sortOrder === "number" && !Number.isNaN(body.sortOrder)
      ? body.sortOrder
      : null;

  const payload = {
    name: body.name,
    displayName: body.displayName,
    category: body.category,
    widgetRoles: body.widgetRoles,
    sortOrder: sortPayload,
    ...(body.roleOrders ? { roleOrders: body.roleOrders } : {}),
  };

  widgetsDebug(`PUT /api/widgets/settings/${id} → request`, {
    tenantId,
    settingId: id,
    payload,
  });

  const res = await fetch(`${apiBaseUrl}/api/widgets/settings/${id}`, {
    method: "PUT",
    headers: {
      ...authHeaders(accessToken, tenantId),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload as Record<string, unknown>),
  });

  if (!(res.ok || res.status === 204)) {
    const errText = await res.text();
    throw new Error(
      `PUT /api/widgets/settings/${id} failed: ${res.status} ${errText || res.statusText}`,
    );
  }

  widgetsDebug(`PUT /api/widgets/settings/${id} ← response`, {
    status: res.status,
    ok: res.ok,
  });
}

export interface FetchWidgetDataBody {
  keys: string[];
  /** API allows null when not scoped to a user. */
  userId?: number | string | null;
  studentId?: number | string | null;
}

type WidgetDataFetchResult =
  | { ok: true; items: WidgetDataItemFromApi[] }
  | { ok: false; status: number; body: string };

async function postWidgetData(
  accessToken: string,
  tenantId: number,
  keys: string[],
  userId: number | string | null,
  studentId: number | string | null,
): Promise<WidgetDataFetchResult> {
  const body: FetchWidgetDataBody = {
    keys,
    userId,
    studentId,
  };

  const res = await fetch(`${apiBaseUrl}/api/widgets/data`, {
    method: "POST",
    headers: {
      ...authHeaders(accessToken, tenantId),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errBody = await res.text();
    return { ok: false, status: res.status, body: errBody || res.statusText };
  }

  const parsed = await parseJsonSafe<WidgetDataItemFromApi[]>(res);
  return { ok: true, items: Array.isArray(parsed) ? parsed : [] };
}

/** Request up to five keys per call (API limit). Returns merged array. */
export async function fetchWidgetDataBatched(
  accessToken: string,
  tenantId: number,
  keys: string[],
  options?: Pick<FetchWidgetDataBody, "userId" | "studentId">,
): Promise<WidgetDataItemFromApi[]> {
  const unique = [...new Set(keys.filter((k) => k.trim().length > 0))];
  const batches: string[][] = [];
  for (let i = 0; i < unique.length; i += 5) {
    batches.push(unique.slice(i, i + 5));
  }
  if (batches.length === 0) return [];

  const userId = options?.userId ?? null;
  const studentId = options?.studentId ?? null;

  const results: WidgetDataItemFromApi[] = [];
  /* eslint-disable no-await-in-loop */
  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    const batch = batches[batchIndex];
    widgetsDebug("POST /api/widgets/data → request batch", {
      tenantId,
      keys: batch,
      batchIndex,
      batchCount: batches.length,
    });

    const batchResult = await postWidgetData(
      accessToken,
      tenantId,
      batch,
      userId,
      studentId,
    );

    if (batchResult.ok) {
      widgetsDebug("POST /api/widgets/data ← batch response", {
        keysReturned: batchResult.items.map((p) => p.key),
        dataPreview: batchResult.items.map((p) => ({
          key: p.key,
          $type: p.data?.$type,
          total: p.data?.total,
        })),
      });
      results.push(...batchResult.items);
      continue;
    }

    widgetsDebug("POST /api/widgets/data batch failed (skipped)", {
      status: batchResult.status,
      keys: batch,
      preview: batchResult.body.slice(0, 300),
    });
  }
  /* eslint-enable no-await-in-loop */

  widgetsDebug("POST /api/widgets/data done", {
    totalItems: results.length,
  });
  return results;
}
